import api from './api'
import type { employee, EmployeeResponse } from '@/types/employee'

export interface SyncMonthAssignmentsResult {
	synced_count: number
	total_months: number
	start_year: number
	years_covered: number[]
}

export const employeeService = {
	/**
	 * Get all employees
	 */
	async getEmployees(): Promise<employee[]> {
		try {
			const response = await api.get<EmployeeResponse>('/users/employees')
			return response.data.data
		} catch (error) {
			console.error('Error fetching employees:', error)
			throw error
		}
	},

	/**
	 * Get a specific employee by ID
	 */
	async getEmployee(id: number): Promise<employee> {
		try {
			const response = await api.get<{ success: boolean; data: employee }>(`/users/${id}`)
			return response.data.data
		} catch (error) {
			console.error('Error fetching employee:', error)
			throw error
		}
	},

	/**
	 * Update employee details
	 */
	async updateEmployee(id: number, data: {
		first_name: string
		last_name: string
		middle_name?: string | null
		email: string
		password?: string
		password_confirmation?: string
	}): Promise<employee> {
		try {
			const response = await api.put<{ success: boolean; data: employee }>(`/users/${id}`, data)
			return response.data.data
		} catch (error) {
			console.error('Error updating employee:', error)
			throw error
		}
	},

	/**
	 * Update employee password only
	 */
	async updateEmployeePassword(id: number, data: {
		password: string
		password_confirmation: string
	}): Promise<employee> {
		try {
			const response = await api.put<{ success: boolean; data: employee }>(`/users/${id}/password`, data)
			return response.data.data
		} catch (error) {
			console.error('Error updating employee password:', error)
			throw error
		}
	},

	/**
	 * Update user status (activate/deactivate)
	 */
	async updateUserStatus(id: number, status: 0 | 1): Promise<{ user_details_id: number; status: string }> {
		try {
			const response = await api.put<{
				success: boolean;
				data: { user_details_id: number; status: string }
			}>(`/users/${id}/status`, { status })
			return response.data.data
		} catch (error) {
			console.error('Error updating user status:', error)
			throw error
		}
	},

	/**
	 * Sync month assignments for all employees
	 */
	async syncMonthAssignments(startYear?: number): Promise<{ message: string; data: SyncMonthAssignmentsResult }> {
		try {
			const payload: Record<string, number> = {}
			if (startYear !== undefined) {
				payload.start_year = startYear
			}

			const response = await api.post<{
				success: boolean;
				message: string;
				data: SyncMonthAssignmentsResult
			}>('/users/sync-month-assignments', payload)

			return {
				message: response.data.message,
				data: response.data.data
			}
		} catch (error) {
			console.error('Error syncing month assignments:', error)
			throw error
		}
	}
}

export default employeeService
